import {AnalyticsClient, getOptionsFromMeta} from '@github/hydro-analytics-client'
import type {Context} from '@github/hydro-analytics-client'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import safeStorage from '@github-ui/safe-storage'
import {getItem as getSessionItem} from '@github-ui/safe-storage/session-storage'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {getSoftNavReferrer, SOFT_NAV_REFERRER} from '@github-ui/soft-nav/utils'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {isStaff} from '@github-ui/stats'
import {logSendEvent} from './dev-logger'
const {getItem} = safeStorage('localStorage')

declare const process: {
  env: {
    NODE_ENV: string
  }
}

const dimensionPrefix = 'dimension_'
const DATADOG_RUM_CONTEXT_KEYS = ['category', 'action', 'label'] as const
let hydroAnalyticsClient: AnalyticsClient | undefined
let contentFreeAnalyticsClient: AnalyticsClient | undefined
const MAX_BATCH_SIZE = 10
const IDLE_TIMEOUT = 5000
const MAX_CONTENT_FREE_BYTES = 60_000
const MAX_CONTENT_FREE_FETCHES = 4
let contentFreeBatch: ContentFreeEntry[] | undefined
let contentFreeTimer: ReturnType<typeof setTimeout> | undefined
let contentFreeFetches = 0
let contentFreeDiagnosticReported = false

const MARKETING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'scid']

function safeGetOptionsFromMeta(prefix: string) {
  try {
    return getOptionsFromMeta(prefix)
  } catch {
    return undefined
  }
}

const initOptions = safeGetOptionsFromMeta('octolytics')
if (initOptions) {
  // Remove the base context because meta tags can change as the user navigates with Turbo
  // These will be folded in for each event & page view
  delete initOptions.baseContext
  initOptions.idleTimeout = IDLE_TIMEOUT
  initOptions.maxBatchSize = MAX_BATCH_SIZE
  initOptions.getReferrer = defaultReferrer =>
    getSessionItem(SOFT_NAV_REFERRER) ? getSoftNavReferrer() : defaultReferrer

  hydroAnalyticsClient = new AnalyticsClient(initOptions)
  // Flush before startSoftNav replaces the referrer used by the next batch.
  ssrSafeDocument?.addEventListener(SOFT_NAV_STATE.START, () => hydroAnalyticsClient?.flushBatch())
}

function extendBaseContext(context?: Context) {
  const baseContext = safeGetOptionsFromMeta('octolytics')?.baseContext ?? {}

  if (baseContext) {
    delete baseContext.app_id
    delete baseContext.event_url
    delete baseContext.host

    for (const [key, value] of Object.entries(baseContext)) {
      // some octolytics meta tags are prefixed with dimension-, which we don't need with the new hydro-analytics-client
      if (key.startsWith(dimensionPrefix)) {
        baseContext[key.replace(dimensionPrefix, '')] = value
        delete baseContext[key]
      }
    }
  }

  const visitorMeta = ssrSafeDocument?.querySelector<HTMLMetaElement>('meta[name=visitor-payload]')
  if (visitorMeta) {
    const visitorHash = JSON.parse(atob(visitorMeta.content))
    Object.assign(baseContext, visitorHash)
  }

  const urlParams = new URLSearchParams(window.location.search)
  for (const [key, value] of urlParams) {
    if (MARKETING_PARAMS.includes(key.toLowerCase())) {
      baseContext[key] = value
    }
  }

  // Include additional context from the page
  baseContext.staff = isStaff().toString()

  return Object.assign(baseContext, context)
}

export function sendPageView(context?: Context) {
  hydroAnalyticsClient?.sendPageView(extendBaseContext(context))
}

/**
 * Forces any events queued by sendEvent's default batching to send immediately,
 * bypassing the idle/timeout-based flush schedule. Intended for test utilities
 * that need to observe an event synchronously after triggering it (e.g.
 * expectAnalyticsEvents) — production code should rely on the automatic
 * flush (idle timeout, batch size, visibilitychange, pagehide) instead.
 */
export function flushBatchedEvents() {
  hydroAnalyticsClient?.flushBatch()
  contentFreeAnalyticsClient?.flushBatch()
  flushContentFreeEvents()
}

export type SendEventContext = Record<string, string | number | boolean | undefined | null>
type DeliveryError = () => void | Promise<void>
type SendEventOptions = {batched?: boolean; page?: string; privacy?: 'content_free'; onError?: DeliveryError}
type HydroEventPayload = {
  events: Array<{
    page: string
    title: string
    context: Context
    type: string
  }>
}
type HydroPayloadSender = {
  send: (payload: HydroEventPayload) => void
}
type ContentFreeEntry = {event: HydroEventPayload['events'][number]; onError?: DeliveryError}

function contentFreePayload(entries: readonly ContentFreeEntry[]): string {
  return JSON.stringify({
    client_id: hydroAnalyticsClient?.clientId,
    events: entries.map(entry => entry.event),
    request_context: {referrer: ''},
  })
}

async function contentFreeDeliveryFailed(entries: readonly ContentFreeEntry[]): Promise<void> {
  const callbacks = new Set(entries.map(entry => entry.onError))
  for (const callback of callbacks) {
    if (callback) {
      try {
        await callback()
        continue
      } catch {
        // Never include a callback's thrown value in a delivery diagnostic.
      }
    }
    if (!contentFreeDiagnosticReported) {
      contentFreeDiagnosticReported = true
      // eslint-disable-next-line no-console
      console.error('Content-free analytics delivery could not be observed')
    }
  }
}

async function deliverContentFree(entries: readonly ContentFreeEntry[]): Promise<void> {
  let ownsFetch = false
  try {
    if (!hydroAnalyticsClient) {
      await contentFreeDeliveryFailed(entries)
      return
    }
    const body = contentFreePayload(entries)
    if (new TextEncoder().encode(body).byteLength > MAX_CONTENT_FREE_BYTES) {
      await contentFreeDeliveryFailed(entries)
      return
    }
    try {
      if (navigator.sendBeacon?.(hydroAnalyticsClient.collectorUrl, body)) return
    } catch {
      // Fall through to the observable unload-safe fetch below.
    }
    if (contentFreeFetches >= MAX_CONTENT_FREE_FETCHES) {
      await contentFreeDeliveryFailed(entries)
      return
    }
    contentFreeFetches++
    ownsFetch = true
    const response = await fetch(hydroAnalyticsClient.collectorUrl, {
      method: 'POST',
      cache: 'no-cache',
      headers: {'Content-Type': 'application/json'},
      body,
      keepalive: true,
    })
    if (!response.ok) await contentFreeDeliveryFailed(entries)
  } catch {
    await contentFreeDeliveryFailed(entries)
  } finally {
    if (ownsFetch) contentFreeFetches--
  }
}

function flushContentFreeEvents(): void {
  if (!contentFreeBatch) return
  const entries = contentFreeBatch
  contentFreeBatch = undefined
  if (contentFreeTimer !== undefined) clearTimeout(contentFreeTimer)
  contentFreeTimer = undefined
  window.removeEventListener('pagehide', flushExitingContentFreeEvents)
  document.removeEventListener('visibilitychange', flushHiddenContentFreeEvents)
  document.removeEventListener(SOFT_NAV_STATE.START, flushContentFreeEvents)
  void deliverContentFree(entries)
}

function flushHiddenContentFreeEvents(): void {
  if (document.visibilityState === 'hidden') flushContentFreeEvents()
}

function flushExitingContentFreeEvents(): void {
  flushContentFreeEvents()
  queueMicrotask(flushContentFreeEvents)
}

function enqueueContentFree(entry: ContentFreeEntry, batched: boolean): void {
  if (!batched) {
    void deliverContentFree([entry])
    return
  }
  if (new TextEncoder().encode(contentFreePayload([entry])).byteLength > MAX_CONTENT_FREE_BYTES) {
    void contentFreeDeliveryFailed([entry])
    return
  }
  if (
    contentFreeBatch &&
    new TextEncoder().encode(contentFreePayload([...contentFreeBatch, entry])).byteLength > MAX_CONTENT_FREE_BYTES
  ) {
    flushContentFreeEvents()
  }
  if (!contentFreeBatch) {
    contentFreeBatch = []
    window.addEventListener('pagehide', flushExitingContentFreeEvents)
    document.addEventListener('visibilitychange', flushHiddenContentFreeEvents)
    document.addEventListener(SOFT_NAV_STATE.START, flushContentFreeEvents)
    contentFreeTimer = setTimeout(flushContentFreeEvents, IDLE_TIMEOUT)
  }
  contentFreeBatch.push(entry)
  if (contentFreeBatch.length >= MAX_BATCH_SIZE) flushContentFreeEvents()
}

export function currentCatalogService() {
  return ssrSafeDocument?.head?.querySelector<HTMLMetaElement>('meta[name="current-catalog-service"]')?.content
}

function datadogRumContext(context: Context): Context | undefined {
  const rumContext: Context = {}
  for (const key of DATADOG_RUM_CONTEXT_KEYS) {
    const value = context[key]
    if (value !== undefined && value !== null) rumContext[key] = value
  }

  return Object.keys(rumContext).length > 0 ? rumContext : undefined
}

async function mirrorEventToDatadogRum(type: string, context: Context) {
  try {
    const {addDatadogRumTelemetryAction} = await import('@github-ui/datadog-rum')
    await addDatadogRumTelemetryAction(type, datadogRumContext(context))
  } catch {
    // Optional telemetry must not affect Hydro events.
  }
}

export function sendEvent(
  type: string,
  context: SendEventContext = {},
  {batched = true, page, privacy, onError}: SendEventOptions = {},
) {
  const service = currentCatalogService()
  const cleanContext: Context = service && privacy !== 'content_free' ? {service} : {}
  const typeWithFallback = type || 'unknown'

  for (const [key, value] of Object.entries(context)) {
    if (value !== undefined && value !== null) {
      cleanContext[key] = `${value}`
    }
  }

  if (privacy === 'content_free') {
    if (!hydroAnalyticsClient) throw new Error('Hydro is unavailable for content-free telemetry')
    if (typeWithFallback.startsWith('one_copilot.')) {
      enqueueContentFree(
        {
          event: {
            type: typeWithFallback,
            context: cleanContext,
            title: 'Copilot',
            page: typeof window === 'undefined' ? '/copilot' : new URL('/copilot', window.location.origin).toString(),
          },
          ...(onError ? {onError} : {}),
        },
        batched,
      )
    } else {
      contentFreeAnalyticsClient ??= new AnalyticsClient({
        collectorUrl: hydroAnalyticsClient.collectorUrl,
        clientId: hydroAnalyticsClient.clientId,
        getReferrer: () => '',
      })
      sendEventWithPage(typeWithFallback, cleanContext, '/copilot', contentFreeAnalyticsClient, 'Copilot')
    }
    if (isFeatureEnabled('datadog_rum_hydro_events')) {
      mirrorEventToDatadogRum(typeWithFallback, cleanContext)
    }
    return
  }

  if (hydroAnalyticsClient) {
    const fullContext = extendBaseContext(cleanContext)
    if (page) {
      sendEventWithPage(typeWithFallback, fullContext, page)
    } else if (batched) {
      hydroAnalyticsClient.sendBatchedEvent(typeWithFallback, fullContext)
    } else {
      hydroAnalyticsClient.sendEvent(typeWithFallback, fullContext)
    }

    if (isFeatureEnabled('datadog_rum_hydro_events')) {
      mirrorEventToDatadogRum(typeWithFallback, cleanContext)
    }

    if (process.env.NODE_ENV === 'development') {
      debugSendEvent(typeWithFallback, fullContext)
    }

    if (getItem('stats-dev-logger') === 'true') {
      logSendEvent(typeWithFallback, fullContext)
    }
  }
}

function sendEventWithPage(
  eventType: string,
  eventContext: Context,
  eventPage: string,
  client = hydroAnalyticsClient,
  title = ssrSafeDocument?.title ?? '',
) {
  if (!client) return

  const normalizedPage =
    typeof window === 'undefined' ? eventPage : new URL(eventPage, window.location.origin).toString()
  const event = {
    page: normalizedPage,
    title,
    context: eventContext,
    type: eventType,
  }

  // The underlying client otherwise reads `location.href` into the top-level page field.
  ;(client as unknown as HydroPayloadSender).send({events: [event]})
}

export function stringifyObjectValues(obj: object) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, JSON.stringify(v)]))
}

function debugSendEvent(type: string, context: Context) {
  if (getItem('hydro-debug.send-event') === 'true') {
    // eslint-disable-next-line no-console
    console.group('[hydro-debug.send-event]')
    // eslint-disable-next-line no-console
    console.log({type, context})
    performance.mark('hydroAnalytics#sendEvent', {detail: {type, context}})
    // eslint-disable-next-line no-console
    console.groupEnd()
  }
}
